import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";

import { SubscriberChangesPane } from "./SubscriberChangesPane";
import { SubscriberDriftPane } from "./SubscriberDriftPane";
import {
  driftCount, driftQuery, searchForSubscriberPane, searchForSubscriberRegion, subscriberParams, SUBSCRIBER_PANES, SUBSCRIBER_REGIONS,
  SUBSCRIBERS_COPY as T, type SubscriberPane,
} from "./subscribers";

/**
 * The Subscribers tab (C1 v1.33, N9a release 1): the page head, v26's two panes as a segmented control (Price changes, the
 * default, and Drift, the pane in the URL as `pp_spane`) and the UK and EU stores as the Price Run's region (`pp_region`). The
 * Drift label carries how many subscriptions are off the price once the drift read has answered. Arriving with the Price Run's
 * UAE or All, the tab shows the EU store and says so in one line. Read-only: every control
 * changes only what is shown, so each carries `data-nonmutating` and a view-only person keeps them all.
 */
export function SubscribersTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const params = subscriberParams(search);
  const go = (next: Record<string, unknown>) => void navigate({ to: ".", search: next, replace: true });
  const attention = useQuery(driftQuery(params.market, "attention", 0));
  const label = (key: SubscriberPane) => (key === "drift" && attention.data ? T.driftWithCount(driftCount(attention.data.summary)) : T.panes[key]);
  return (
    <div className="flex flex-col gap-4">
      <div className="page-head"><h2 className="text-lg font-semibold text-ks-ink">{T.title}</h2><p className="text-sm text-ks-grey-1">{T.desc}</p></div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div role="group" aria-label="Pane" className="flex gap-1">
          {SUBSCRIBER_PANES.map((key) => (
            <Button key={key} size="sm" variant={key === params.pane ? "default" : "outline"} aria-pressed={key === params.pane} data-nonmutating onClick={() => go(searchForSubscriberPane(search, key))}>
              {label(key)}
            </Button>
          ))}
        </div>
        <div role="group" aria-label={T.region} className="flex gap-1">
          {SUBSCRIBER_REGIONS.map((key) => (
            <Button key={key} size="sm" variant={key === params.region ? "default" : "outline"} aria-pressed={key === params.region} data-nonmutating onClick={() => go(searchForSubscriberRegion(search, key))}>
              {T.regionLabel[key]}
            </Button>
          ))}
        </div>
      </div>
      {params.otherRegion && <p role="status" className="text-sm text-ks-grey-1">{T.otherRegion}</p>}
      {params.pane === "drift" ? <SubscriberDriftPane areaName={areaName} /> : <SubscriberChangesPane areaName={areaName} />}
    </div>
  );
}
